/*
 * Testimonia — PDP gallery reveal
 *
 * Companion to snippets/testimonia-pdp-gallery-filter.liquid, which hides every variant-attached
 * media item in CSS. This script reveals the media of the variant the customer actually selects.
 *
 * Contract: docs/testimonia-pdp-contract.md §5.1
 *
 * Two things make this necessary rather than pure CSS:
 *   - the reveal target changes with the selection, and
 *   - the theme re-fetches and re-renders the gallery on variant change
 *     (applyProductMediaUpdate, assets/global.js:2755), replacing the elements we marked.
 *
 * The reveal class is only applied after a real selection change, so the first paint shows the
 * product's standard photos and nothing else — which is the point of the feature.
 */
(function () {
  'use strict';

  var REVEAL = 'tpdp-media-reveal';

  var map = null;
  var currentMediaId = null;
  var wrapperObserver = null;

  // The reveal only starts after the customer actually changes something, so the first paint
  // shows the standard photos and no variant image.
  var hasInteracted = false;

  function init() {
    // Runs on every PDP, independently of the variant-filtering feature below.
    fixGallerySizes();
    bindPanelFormats();
    watchInitialAutoHeight();

    // Needed by fixAutoHeight() on every PDP, not just products where the variant-media filter
    // below has data to emit (see watchGalleryRerender's comment for why this can't be gated
    // behind the #tpdp-gallery-data check the way watchVariant() is).
    watchGalleryRerender();

    var el = document.getElementById('tpdp-gallery-data');
    if (!el) return;

    try {
      map = JSON.parse(el.textContent).variantMedia || {};
    } catch (e) {
      return;
    }

    watchVariant();
  }

  /**
   * Panel format picker: selecting a format navigates to that panel product.
   *
   * The control is rendered as radios so it inherits the theme's checked styling verbatim
   * (snippets/testimonia-panel-formats.liquid); each value carries the destination URL.
   * Delegated on document so it survives the block being re-rendered on variant change.
   */
  function bindPanelFormats() {
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || t.tagName !== 'INPUT') return;
      if (t.getAttribute('name') !== 'tca-panel-format') return;
      if (t.value) window.location.href = t.value;
    });
  }

  // ── Right-sized gallery images ─────────────────────────────────────────────

  /**
   * Correct the `sizes` attribute the base theme emits for gallery images.
   *
   * `snippets/product-thumbnail.liquid` declares
   *     (min-width: 1100px) 100vw, ...
   * but on desktop the gallery column is roughly 45% of the viewport, not 100%. Measured on
   * cuadro-tucanes at a 1598px viewport: the slot renders at 719px while the browser downloads
   * the 1100px candidate — about 1.5x more pixels than are displayed.
   *
   * Only the >=1100px clause is wrong; the narrower breakpoints already scale (0.54, 0.55), so
   * they are left alone.
   *
   * The default is 60vw rather than the measured ~45vw deliberately: `sizes` is multiplied by the
   * device pixel ratio when the browser picks from the srcset, and the extra headroom keeps the
   * image from looking soft on 2x displays. Tunable from the Theme Editor.
   *
   * Timing: every gallery image is `loading="lazy"` (product-thumbnail.liquid hardcodes it), so
   * rewriting `sizes` here still lands before the below-the-fold images are fetched.
   */
  function fixGallerySizes() {
    var cfg = document.querySelector('[data-tpdp-gallery-vw]');
    var vw = cfg ? parseInt(cfg.getAttribute('data-tpdp-gallery-vw'), 10) : 60;
    if (!vw || vw < 10 || vw > 100) vw = 60;

    var imgs = document.querySelectorAll('.product__media-wrapper img[sizes]');
    for (var i = 0; i < imgs.length; i++) {
      var current = imgs[i].getAttribute('sizes');
      if (!current || current.indexOf('100vw') === -1) continue;
      var next = current.replace(/\(min-width:\s*1100px\)\s*100vw/, '(min-width: 1100px) ' + vw + 'vw');
      if (next !== current) imgs[i].setAttribute('sizes', next);
    }
  }

  // ── Mobile "auto height" swiper collapse after a variant re-render ─────────

  /**
   * Force the mobile gallery's swiper wrapper to the active slide's real height.
   *
   * Repro: on `show_only_variant_media` templates (cuadro, canvas, panel, papiro,
   * set-de-cuadros), switching a swatch on mobile makes the theme replace the gallery HTML and
   * rebuild the Swiper instance (assets/global.js applyProductMediaUpdate -> reinitSlider).
   * Swiper's `autoHeight` option measures the active slide's `offsetHeight` right at that moment
   * to size `.swiper-wrapper` — but `.swiper-slide` carries `height: 100%` in this theme's CSS,
   * which resolves against the wrapper's own height. The wrapper has no height yet at that point,
   * so the measurement is 0, gets written back as the wrapper's inline height, and nothing ever
   * recalculates it afterwards — the gallery stays collapsed (and everything below it jumps up
   * to fill the gap) until the page is reloaded. Calling Swiper's own `update()` /
   * `updateAutoHeight()` again afterwards does not help: it re-reads the same circular 100%-of-0
   * chain and gets 0 again.
   *
   * Reproduced with no lifestyle image at all (both metafields empty), so this predates and is
   * unrelated to the lifestyle-image feature above — it's a bug in the stock mobile gallery under
   * `show_only_variant_media`.
   *
   * Fix: measure the slide's actual photo (`.product__media`, sized by its own aspect-ratio
   * padding-top — unaffected by the wrapper's collapsed height) and write that real number onto
   * `.swiper-wrapper` ourselves. Once the wrapper has a real, non-zero height, `.swiper-slide`'s
   * `height: 100%` resolves correctly against it and the circularity is broken.
   */
  function fixAutoHeight() {
    var lists = document.querySelectorAll('.product__media-list.swiper-autoheight');
    for (var i = 0; i < lists.length; i++) syncSwiperHeight(lists[i]);
  }

  /**
   * Same fix as fixAutoHeight(), but for the very first page load instead of a later
   * variant-driven re-render.
   *
   * On a cold load, this script (deferred, from testimonia-pdp-styles.liquid, placed last in the
   * template on purpose) can still run before the base theme's own slider script has finished
   * turning `.product__media-list` into a working Swiper — `.swiper-slide-active` is a class
   * Swiper itself assigns, not something the server-rendered HTML has, so a single check at
   * DOMContentLoaded can miss it entirely and silently fix nothing, leaving the gallery collapsed
   * (and the page reading as if it opened already scrolled past the photo, straight to the text)
   * until something else happens to touch the gallery. Instead of guessing a delay, watch each
   * gallery list for its active slide to actually appear, then fix it right then.
   */
  function watchInitialAutoHeight() {
    var lists = document.querySelectorAll('.product__media-list');
    for (var i = 0; i < lists.length; i++) armAutoHeightWatch(lists[i]);
  }

  function armAutoHeightWatch(list) {
    if (list.querySelector('.swiper-slide-active')) {
      syncSwiperHeight(list);
      return;
    }

    var obs = new MutationObserver(function () {
      if (list.querySelector('.swiper-slide-active')) {
        syncSwiperHeight(list);
        obs.disconnect();
      }
    });
    obs.observe(list, { attributes: true, attributeFilter: ['class'], subtree: true });
  }

  function syncSwiperHeight(list) {
    var swiperWrapperEl = list.querySelector('.swiper-wrapper');
    var activeSlide = list.querySelector('.swiper-slide-active');
    if (!swiperWrapperEl || !activeSlide) return;

    var media = activeSlide.querySelector('.product__media') || activeSlide;

    var apply = function () {
      var height = media.offsetHeight;
      if (height > 0) swiperWrapperEl.style.height = height + 'px';
    };

    apply();

    // The photo may still be decoding when the gallery HTML lands, in which case the measurement
    // above reads its pre-image placeholder height — correct once it loads.
    var img = activeSlide.querySelector('img');
    if (img && !img.complete) img.addEventListener('load', apply, { once: true });
  }

  // ── Which variant is selected ──────────────────────────────────────────────

  function selectedVariantId() {
    var input = document.querySelector('form[action*="/cart/add"] input[name="id"]');
    return input && input.value ? String(input.value) : null;
  }

  function onVariantChange() {
    var id = selectedVariantId();
    if (!id) return;

    hasInteracted = true;
    var mediaId = Object.prototype.hasOwnProperty.call(map, id) ? String(map[id]) : null;
    currentMediaId = mediaId;
    applyReveal();
  }

  /**
   * Which media the theme itself decided to show.
   *
   * `map` is built from `product.variants`, which Liquid caps at 250. cuadro-tucanes has 412,
   * so selecting a variant past that cap yields no entry — and without this fallback the chosen
   * image stays hidden by our CSS and the gallery looks frozen.
   *
   * The theme always renders `selected_or_first_available_variant.featured_media` as the first
   * item of the media list (main-product-media-content.liquid:39-66), and re-renders it on every
   * variant change, so the first item is the authoritative answer regardless of the cap.
   */
  function themeSelectedMediaId() {
    var first = document.querySelector('.product__media-list .product__media-item[data-media-id]');
    if (!first) return null;
    var attr = first.getAttribute('data-media-id') || '';
    var idx = attr.lastIndexOf('-');
    return idx === -1 ? null : attr.slice(idx + 1);
  }

  function applyReveal() {
    // Clear the previous reveal wherever it landed, including elements the theme just re-rendered.
    var previous = document.querySelectorAll('.' + REVEAL);
    for (var i = 0; i < previous.length; i++) previous[i].classList.remove(REVEAL);

    if (!hasInteracted) return;

    // Fall back to the theme's own choice when the variant is past Liquid's 250-variant cap.
    var target = currentMediaId || themeSelectedMediaId();
    if (!target) return;

    var sel = '[data-media-id$="-' + target + '"], [data-media-sub-id$="-' + target + '"]';
    var targets = document.querySelectorAll(sel);
    for (var j = 0; j < targets.length; j++) targets[j].classList.add(REVEAL);
  }

  // ── Signals ────────────────────────────────────────────────────────────────

  /**
   * Listen for variant selection.
   *
   * Two earlier signals here were dead, which is why changing paspartú changed nothing:
   *
   *   - A MutationObserver with `attributeFilter: ['value']` on the hidden input never fired.
   *     The theme assigns `input.value = this.currentVariant.id` (global.js:2461), and a property
   *     assignment mutates no attribute, so no mutation record is produced.
   *
   *   - `variant:change` (global.js:2167) is only dispatched at the end of the normal update
   *     path. For high-variant products the code returns early at global.js:2132-2137, so on a
   *     412-variant product like cuadro-tucanes that event does not arrive.
   *
   * The reliable signal is the bubbling `change` the theme fires on the input immediately after
   * assigning it (global.js:2462). Bound on `document` in the capture phase so it survives the
   * form being re-rendered by renderProductInfo().
   */
  function watchVariant() {
    document.addEventListener('variant:change', onVariantChange);

    document.addEventListener(
      'change',
      function (e) {
        var t = e.target;
        if (t && t.tagName === 'INPUT' && t.getAttribute('name') === 'id') onVariantChange();
      },
      true
    );
  }

  /**
   * Re-apply after the theme swaps the gallery markup.
   *
   * applyProductMediaUpdate() replaces the media elements with freshly fetched HTML, which drops
   * our reveal class (fixed by re-running applyReveal) and leaves the mobile swiper's auto-height
   * collapsed (fixed by re-running fixAutoHeight — see its own comment for why).
   */
  function watchGalleryRerender() {
    var wrapper = document.querySelector('.product__media-wrapper');
    if (!wrapper || wrapperObserver) return;

    var pending = null;
    wrapperObserver = new MutationObserver(function (records) {
      // Ignore the class/style mutations we cause ourselves.
      var structural = records.some(function (r) { return r.type === 'childList'; });
      if (!structural) return;

      // A structural gallery change only happens when the theme re-renders after a variant
      // update, so this is itself proof of a selection — and the last line of defence if every
      // event above fails to reach us.
      hasInteracted = true;

      clearTimeout(pending);
      pending = setTimeout(function () {
        applyReveal();
        fixAutoHeight();
      }, 60);
    });
    wrapperObserver.observe(wrapper, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
